import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function ELBWallet() {
  const [wallet, setWallet] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Conversion form
  const [convertForm, setConvertForm] = useState({
    direction: 'eur_to_elb',
    amount: '',
    account_id: ''
  });
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    to_wallet_address: '',
    amount_elb: '',
    notes: ''
  });

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get or create wallet
      const walletRes = await axios.get(`${API_URL}/elb/wallet`, config);
      setWallet(walletRes.data);
      
      // Get user accounts for conversion
      const accountsRes = await axios.get(`${API_URL}/accounts`, config);
      setAccounts(accountsRes.data.filter(acc => acc.currency === 'EUR' && acc.status === 'active'));
      
      // Get transactions
      const transRes = await axios.get(`${API_URL}/elb/transactions`, config);
      setTransactions(transRes.data.transactions || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    
    if (!convertForm.amount || !convertForm.account_id) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/elb/convert`, convertForm, config);
      alert(response.data.message);
      setConvertForm({ ...convertForm, amount: '' });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la conversion');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!transferForm.to_wallet_address || !transferForm.amount_elb) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/elb/transfer`, transferForm, config);
      alert(`Transfert réussi ! ${response.data.amount} e-£B envoyés à ${response.data.to}`);
      setTransferForm({ to_wallet_address: '', amount_elb: '', notes: '' });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors du transfert');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  const walletData = wallet?.wallet || wallet || {};
  const balanceELB = walletData.balance_elb || 0;
  const balanceEUR = wallet?.balance_eur_equivalent || 0;
  const walletAddress = walletData.wallet_address || 'N/A';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            💰 Portefeuille e-£B (Livre Bretonne Électronique)
          </h1>
          <p className="text-blue-200">Votre monnaie électronique bretonne</p>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-2xl shadow-2xl p-8 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-yellow-900 text-sm font-semibold mb-1">Solde disponible</p>
              <h2 className="text-5xl font-bold text-white mb-2">
                {balanceELB.toFixed(2)} <span className="text-3xl">£B</span>
              </h2>
              <p className="text-yellow-900 text-sm">
                ≈ {balanceEUR.toFixed(2)} EUR
              </p>
            </div>
            <div className="text-right">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <p className="text-xs text-yellow-900 font-semibold">Taux</p>
                <p className="text-sm font-bold text-white">1 £B = 1,25 €</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-yellow-700/30 pt-4">
            <p className="text-xs text-yellow-900 font-semibold mb-1">Adresse du portefeuille</p>
            <p className="text-sm font-mono text-white bg-black/20 rounded px-3 py-2 break-all">
              {walletAddress}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-2xl shadow-xl">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📊 Vue d'ensemble
              </button>
              <button
                onClick={() => setActiveTab('convert')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'convert'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                🔄 Conversion
              </button>
              <button
                onClick={() => setActiveTab('transfer')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'transfer'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                💸 Envoyer
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📜 Historique
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-xs text-blue-600 font-semibold mb-1">Solde e-£B</p>
                    <p className="text-2xl font-bold text-blue-900">{balanceELB.toFixed(2)} £B</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-xs text-green-600 font-semibold mb-1">Équivalent EUR</p>
                    <p className="text-2xl font-bold text-green-900">{balanceEUR.toFixed(2)} €</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-xs text-purple-600 font-semibold mb-1">Transactions</p>
                    <p className="text-2xl font-bold text-purple-900">{transactions.length}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    💡 À propos de la Livre Bretonne Électronique (e-£B)
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span><strong>Taux fixe:</strong> 1 e-£B = 1,25 EUR (même parité que la Livre Bretonne physique)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span><strong>Blockchain simulée:</strong> Transactions sécurisées avec hash cryptographique</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span><strong>Conversion instantanée:</strong> Convertissez vos EUR en e-£B et vice-versa à tout moment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span><strong>Transferts gratuits:</strong> Envoyez des e-£B à d'autres utilisateurs sans frais</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Convert Tab */}
            {activeTab === 'convert' && (
              <form onSubmit={handleConvert} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Direction de conversion *
                  </label>
                  <select
                    value={convertForm.direction}
                    onChange={(e) => setConvertForm({ ...convertForm, direction: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="eur_to_elb">EUR → e-£B (1 EUR = 0,8 £B)</option>
                    <option value="elb_to_eur">e-£B → EUR (1 £B = 1,25 EUR)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Compte EUR *
                  </label>
                  <select
                    value={convertForm.account_id}
                    onChange={(e) => setConvertForm({ ...convertForm, account_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionnez un compte</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.account_type} - {acc.balance.toFixed(2)} EUR ({acc.iban})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant {convertForm.direction === 'eur_to_elb' ? 'EUR' : 'e-£B'} *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={convertForm.amount}
                    onChange={(e) => setConvertForm({ ...convertForm, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={convertForm.direction === 'eur_to_elb' ? '100.00' : '80.00'}
                    required
                  />
                  {convertForm.amount && (
                    <p className="text-xs text-gray-600 mt-1">
                      ≈ {convertForm.direction === 'eur_to_elb' 
                        ? (parseFloat(convertForm.amount) * 0.8).toFixed(2) + ' e-£B'
                        : (parseFloat(convertForm.amount) * 1.25).toFixed(2) + ' EUR'
                      }
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                >
                  🔄 Convertir
                </button>
              </form>
            )}

            {/* Transfer Tab */}
            {activeTab === 'transfer' && (
              <form onSubmit={handleTransfer} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse du portefeuille destinataire *
                  </label>
                  <input
                    type="text"
                    value={transferForm.to_wallet_address}
                    onChange={(e) => setTransferForm({ ...transferForm, to_wallet_address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="0xBZ..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant e-£B *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={balanceELB}
                    value={transferForm.amount_elb}
                    onChange={(e) => setTransferForm({ ...transferForm, amount_elb: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="10.00"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Solde disponible: {balanceELB.toFixed(2)} e-£B
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note (optionnel)
                  </label>
                  <textarea
                    value={transferForm.notes}
                    onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Description du transfert..."
                    rows="3"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                >
                  💸 Envoyer
                </button>
              </form>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    Aucune transaction pour le moment
                  </div>
                ) : (
                  transactions.map((tx, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            tx.transaction_type === 'mint' ? 'bg-purple-100 text-purple-800' :
                            tx.transaction_type === 'convert_to_elb' ? 'bg-blue-100 text-blue-800' :
                            tx.transaction_type === 'convert_to_eur' ? 'bg-green-100 text-green-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {tx.transaction_type === 'mint' ? '🏦 Émission' :
                             tx.transaction_type === 'convert_to_elb' ? '🔵 EUR → £B' :
                             tx.transaction_type === 'convert_to_eur' ? '🟢 £B → EUR' :
                             tx.from_wallet === walletAddress ? '📤 Envoi' : '📥 Réception'}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${
                            tx.to_wallet === walletAddress && tx.from_wallet !== walletAddress ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {tx.to_wallet === walletAddress && tx.from_wallet !== walletAddress ? '+' : '-'}
                            {tx.amount_elb.toFixed(2)} £B
                          </p>
                          {tx.euro_amount && (
                            <p className="text-xs text-gray-600">
                              {tx.euro_amount.toFixed(2)} EUR
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-xs text-gray-600 space-y-1">
                        <p><strong>Hash:</strong> <span className="font-mono">{tx.transaction_hash}</span></p>
                        <p><strong>Date:</strong> {new Date(tx.timestamp).toLocaleString('fr-FR')}</p>
                        {tx.notes && <p><strong>Note:</strong> {tx.notes}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ELBWallet;
